import React, { useState, useEffect } from 'react';
import { Plus } from "lucide-react";
import { useNavigate, useLocation } from 'react-router-dom';
import { orderingQuotationService } from "../../ordering/services/orderingQuotationService";
import { showApiError } from "@/lib/toast";

import { LeadExecutionTable } from './lead_ececution_table';
import { LeadExecutionDocumentViewer } from './LeadExecutionDocumentViewer';
import { ActionView, ActionDownload, ActionEdit } from "@/components/ui/LatestTableActions";
import { useRowHighlight } from "@/components/ui/RowHighlight";
import { BASE_URL } from "@/lib/API";
import toast from 'react-hot-toast';

export const OrderingTab = ({ project }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  // Apply row highlighting hook
  const getHighlightClass = useRowHighlight(quotations, '_id', 5000, 'bg-sky-100/80', ['status']);

  const fetchQuotations = async () => {
    try {
      setIsLoading(true);
      const res = await orderingQuotationService.getQuotations();
      const allQuotations = res?.data || [];
      
      // Get all possible IDs for the current project's contact
      const leadIdStr = String(project?.leadId?._id || project?.leadId || project?._id || '');
      const contactCustomId = String(project?.leadId?.customId || project?.leadId?.verveId || '');

      const filtered = allQuotations.filter(q => {
        const qLeadId = String(q.leadId?._id || q.leadId || '');
        const qClientId = String(q.clientId?._id || q.clientId || '');
        const qContactObjId = String(q.contactObj?._id || q.contactObj || '');
        const qContactRef = String(q.contactRef || '');
        const qCustomId = String(q.customId || '');
        const qDocType = q.docType || 'Order';

        if (qDocType !== 'Order') return false;

        return (
          (leadIdStr && (
            qLeadId === leadIdStr ||
            qClientId === leadIdStr ||
            qContactObjId === leadIdStr ||
            qContactRef === leadIdStr
          )) ||
          (contactCustomId && (
            qCustomId === contactCustomId ||
            qContactRef === contactCustomId
          ))
        );
      });
      
      setQuotations(filtered);
    } catch (err) {
      console.error("Error fetching ordering quotations:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project) {
      fetchQuotations();
    }
  }, [project]);

  const handleNavigateToOrdering = () => {
    // Navigate to create order with the project's lead data pre-filled
    navigate('/ordering/create', { state: { contactObj: project?.leadId || project, docType: 'Order' } });
  };

  const handleEdit = (record) => {
    // Navigate to create order but pass the record as editQuotation
    navigate('/ordering/create', { state: { editQuotation: record, docType: 'Order' } });
  };

  const handleView = async (record) => {
    if (!record.pdfUrl) {
      toast.error('No PDF available for this order');
      return;
    }
    try {
      const fullUrl = `${BASE_URL}${record.pdfUrl}`;
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('blum_token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to load PDF');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      setViewerFile({ url: objectUrl, name: record.quotationNo || 'Order' });
      setViewerOpen(true);
    } catch (err) {
      console.error('View failed:', err);
      toast.error('Failed to load order PDF');
    }
  };

  const handleDownload = async (record) => {
    const pdfUrl = record.pdfUrl ? `${BASE_URL}${record.pdfUrl}` : null;
    if (!pdfUrl) {
      toast.error('No PDF available to download');
      return;
    }

    try {
      const response = await fetch(pdfUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('blum_token')}`
        }
      });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = record.quotationNo ? `${record.quotationNo.replace(/\//g, '_')}.pdf` : 'Order.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download file");
    }
  };

  const columns = [
    {
      header: "Order ID",
      render: (record) => (
        <span className="font-semibold text-[#65768e] text-[11px] uppercase tracking-wider whitespace-nowrap">{record.quotationNo || '—'}</span>
      )
    },
    {
      header: "Site",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">{record.site || record.siteName || record.projectName || '—'}</span>
      )
    },
    {
      header: "Category",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold capitalize">{record.category || '—'}</span>
      )
    },
    {
      header: "Quote Prepared By",
      render: (record) => {
        const designer = record.referenceDesigner;
        const designerName = typeof designer === 'object' && designer !== null ? (designer.name || designer.email || '') : designer;
        return (
          <span className="text-[#65768e] text-[11px] font-semibold">{designerName || '—'}</span>
        );
      }
    },
    {
      header: "Created Date",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold whitespace-nowrap">
          {record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
        </span>
      )
    },
    {
      header: "Amount",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">
          ₹ {Number(record.totals?.grandTotal || record.grandTotal || record.totalAmount || 0).toLocaleString('en-IN')}
        </span>
      )
    },
    {
      header: "Status",
      render: (record) => {
        const status = record.status || 'Draft';
        const colorClass =
          status === 'Approved' || status === 'Payment Completed' || status === 'Paid' || status === 'Completed'
            ? 'bg-green-100 text-green-700'
            : status === 'Rejected'
            ? 'bg-red-100 text-red-700'
            : status === 'Pending'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-slate-100 text-slate-700';
        return (
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md whitespace-nowrap ${colorClass}`}>
            {status}
          </span>
        );
      }
    },
    {
      header: "Actions",
      render: (record) => (
        <div className="flex items-center gap-1.5">
          <ActionView onClick={() => handleView(record)} title="View" />
          <ActionEdit onClick={() => handleEdit(record)} title="Edit Order" />
          <ActionDownload onClick={() => handleDownload(record)} title="Download" />
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col animate-in fade-in duration-500">
      <div className="flex flex-col w-full">
        <div className="p-1 mb-2 flex items-center justify-end gap-3">
          <button
            onClick={handleNavigateToOrdering}
            className="flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold shadow-sm transition-colors border border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Create Ordering
          </button>
        </div>
        
        <LeadExecutionTable 
          columns={columns} 
          data={quotations} 
          isLoading={isLoading} 
          getRowClass={(record) => getHighlightClass(record._id)}
        />
      </div>

      <LeadExecutionDocumentViewer 
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewerFile?.url}
        fileName={viewerFile?.name}
      />
    </div>
  );
};

export default OrderingTab;
